'use client';

import type { ComponentProps } from 'react';
import { useMemo, useRef, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Edit, Search, Loader2 } from 'lucide-react';
import {
  useCreateContact,
  useUpdateContact,
  useInfiniteGlobalContacts,
  createGlobalContactAction
} from '@/lib/services/contacts';
import {
  Contact,
  CreateContactRequest,
  UpdateContactRequest
} from '@/lib/services/contacts/types';
import type { GlobalContact } from '@/lib/services/contacts/types';
import { ContactFormCard } from '../contact-form/ContactFormCard';
import { ContactAttributeCreateForm } from '../contact-attribute-create-form';
import {
  useUnsavedChangesGuard,
  UnsavedChangesDialog
} from '@/components/shared/unsaved-changes-guard';
import {
  focusOpenAttributeEditor,
  isUnattributedOutsideInteraction
} from '@/components/attributes';
import { useMatterId } from './hooks/useMatterId';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { SEARCH_DEBOUNCE_MS } from '@/lib/constants';
import { getContactDisplayName } from '@/lib/services/contacts/utils';
import { toast } from '@/lib/toast';
import { useQueryClient } from '@tanstack/react-query';

interface AddContactDialogProps {
  existingContact?: Contact;
  /** Feeds `SheetTrigger asChild`, whose Slot throws on anything but a single element, so this is `ReactElement` rather than `ReactNode` (VALE-1814; see `__tests__/radix-aschild-slot-registry.test.ts`). */
  trigger?: React.ReactElement;
  /**
   * Fired after a successful create or update. **Receives the created contact**
   * where one is available (the matter-context create path), so a host that
   * opened this dialog to pick a contact can adopt it rather than making the
   * reader find it again in a list (VALE-1760). The argument is optional and
   * every existing caller ignores it.
   */
  onSuccess?: (created?: Contact) => void;
  initialIsLegalPersonalRepresentative?: boolean;
  initialIsBeneficiary?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  matterId?: number;
  /** Show search UI to find existing contacts before creating */
  enableSearch?: boolean;
  /** Callback when user selects an existing contact from search */
  onSelectExisting?: (contact: GlobalContact) => void;
}

export function AddContactDialog({
  existingContact,
  trigger,
  onSuccess,
  initialIsLegalPersonalRepresentative = false,
  initialIsBeneficiary = false,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  matterId: propMatterId,
  enableSearch = false,
  onSelectExisting
}: AddContactDialogProps) {
  const isEditMode = !!existingContact;
  // useMatterId must be called unconditionally; prefer the prop if provided.
  const fallbackMatterId = useMatterId();
  const matterIdNumber = propMatterId || fallbackMatterId;
  const hasMatterId = matterIdNumber > 0;

  // Matter-context hooks (safe to call with 0 — they just won't be used)
  const createContact = useCreateContact(matterIdNumber);
  const updateContact = useUpdateContact(
    matterIdNumber,
    existingContact?.id || 0
  );

  const queryClient = useQueryClient();

  // Use controlled state if provided, otherwise manage internally
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Search state (only used when enableSearch is true)
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(!enableSearch);
  const debouncedSearch = useDebouncedValue(searchTerm, SEARCH_DEBOUNCE_MS);

  // Search query (only runs when enableSearch is true and there's a search term)
  const { data: searchData, isLoading: isSearching } =
    useInfiniteGlobalContacts(
      enableSearch && debouncedSearch
        ? { q: debouncedSearch, limit: 10 }
        : undefined,
      { enabled: enableSearch && !!debouncedSearch && !showForm }
    );

  const searchResults =
    searchData && !showForm
      ? (
          (searchData as { pages?: { items: GlobalContact[] }[] }).pages ?? []
        ).flatMap((page) => page.items)
      : [];

  // Create a new contact object with initial values for add mode
  const contactData: Contact = useMemo(() => {
    if (existingContact) {
      return existingContact;
    }
    const now = new Date().toISOString();
    return {
      id: 0,
      matterId: matterIdNumber,
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      isLegalPersonalRepresentative: hasMatterId
        ? initialIsLegalPersonalRepresentative
        : false,
      isBeneficiary: hasMatterId ? initialIsBeneficiary : false,
      legalPersonalRepresentativeRole: undefined,
      primaryIdentificationFileId: undefined,
      secondaryIdentificationFileId: undefined,
      // No seed: `line1` / `city` / `postalCode` are not `Contact['address']`
      // keys, so this seeded four dead keys into every address patch and
      // posted `address: { state: '' }` on every contact created here, where
      // the drawer's add mode posts none (VALE-1505).
      address: undefined,
      bankAccount: {
        accountName: '',
        bsb: '',
        accountNumber: ''
      },
      createdAt: now,
      updatedAt: now
    } as Contact;
  }, [
    existingContact,
    matterIdNumber,
    hasMatterId,
    initialIsLegalPersonalRepresentative,
    initialIsBeneficiary
  ]);

  const handleSave = async (data: Partial<Contact>) => {
    setIsSubmitting(true);
    let createdContact: Contact | undefined;
    try {
      if (hasMatterId) {
        // Matter context: use React Query mutations
        if (isEditMode) {
          await updateContact.mutateAsync(data as UpdateContactRequest);
        } else {
          createdContact = await createContact.mutateAsync(
            data as CreateContactRequest
          );
        }
      } else {
        // Global context: use server action directly
        const result = await createGlobalContactAction(
          data as CreateContactRequest
        );
        if (!result.success) {
          toast.error(result.error || 'Failed to create contact');
          return;
        }
        toast.success('Contact created successfully');
        queryClient.invalidateQueries({ queryKey: ['contacts', 'global'] });
      }
      setOpen(false);
      resetSearch();
      onSuccess?.(createdContact);
    } catch (error) {
      console.error(
        `Error ${isEditMode ? 'updating' : 'creating'} contact:`,
        error
      );
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} contact`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    resetSearch();
  };

  const handleSelectExisting = (contact: GlobalContact) => {
    setOpen(false);
    resetSearch();
    onSelectExisting?.(contact);
  };

  const resetSearch = () => {
    setSearchTerm('');
    setShowForm(!enableSearch);
  };

  // Reset search state when dialog opens
  /**
   * **The create form buffers, so leaving has to be guarded** (VALE-632) — the
   * same three protections `ContactDetailsDrawer` gives its own add mode, for
   * the same reason: since the conversion this surface has inline editors and a
   * buffer that has never been sent anywhere.
   *
   * `editingFieldRef` is a ref rather than state because both readers below run
   * inside an event handler and must be right *before* any render.
   */
  const editingFieldRef = useRef(false);

  const unsavedChanges = useUnsavedChangesGuard({
    // Only the create path buffers; the (vestigial) edit path is RHF and owns
    // its own dirty state, so nothing here arms for it.
    enabled: !isEditMode,
    resetKey: existingContact?.id ?? null,
    open,
    onOpenChange: (nextOpen) => {
      setOpen(nextOpen);
      if (nextOpen) resetSearch();
    }
  });

  /**
   * **Force the open field's commit-on-blur before an outside click dismisses
   * the sheet** (VALE-60, carried across from the drawer). Radix detects the
   * dismiss on `pointerdown`, which fires before the browser's own focus
   * change — so without this the sheet can close before `blur` ever reaches the
   * open editor, discarding what was typed.
   */
  const handlePointerDownOutside = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  /**
   * **Refuse an outside-interaction verdict reached with no attribution**
   * (VALE-1677, extending VALE-1652 off `ai-import-dialog-content.tsx`). This
   * sheet mounts the attribute treatment's composite popovers directly
   * (`ContactAttributeCreateForm`), and their capture-phase commit can unmount
   * the pointerdown target before Radix's dismissable layer looks at it — see
   * `isUnattributedOutsideInteraction`'s own doc for the mechanism. Create-path
   * only, same gate as `handlePointerDownOutside` above: the edit arm is RHF,
   * whose inputs commit on change rather than on a dismissable popover close.
   */
  const handleInteractOutside = (
    event: Parameters<
      NonNullable<ComponentProps<typeof SheetContent>['onInteractOutside']>
    >[0]
  ) => {
    if (isUnattributedOutsideInteraction(event)) event.preventDefault();
  };

  const handleOpenChange = (newOpen: boolean) => {
    // Routed through the guard so a dirty close prompts instead of discarding;
    // it calls back into the real open/reset above when it decides to proceed.
    unsavedChanges.handleOpenChange(newOpen);
  };

  const defaultTrigger = isEditMode ? (
    <Button variant="ghost" size="sm">
      <Edit className="h-4 w-4" />
    </Button>
  ) : (
    <Button size="xs" density="compact" gap="standard">
      <Plus className="h-3.5 w-3.5" />
      Add Contact
    </Button>
  );

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        {(trigger || controlledOpen === undefined) && (
          <SheetTrigger asChild>{trigger || defaultTrigger}</SheetTrigger>
        )}
        <SheetContent
          className="flex w-full flex-col overflow-hidden sm:max-w-2xl"
          /*
          Both guards apply to the create path only — the edit arm is RHF, whose
          inputs are always open and commit on change, so there is no editor to
          revert and no blur to force.
        */
          onEscapeKeyDown={
            isEditMode
              ? undefined
              : (event) => {
                  if (editingFieldRef.current) event.preventDefault();
                }
          }
          onPointerDownOutside={
            isEditMode ? undefined : handlePointerDownOutside
          }
          onInteractOutside={isEditMode ? undefined : handleInteractOutside}
        >
          <SheetHeader>
            <SheetTitle>
              {isEditMode ? 'Edit Contact' : 'Add New Contact'}
            </SheetTitle>
            <SheetDescription>
              {isEditMode
                ? 'Update the contact information below.'
                : enableSearch && !showForm
                  ? 'Search for an existing contact or create a new one.'
                  : 'Enter the contact details below. Fields marked with * are required.'}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 flex-1 overflow-y-auto">
            {/* Search UI (only when enableSearch is true and not showing form) */}
            {enableSearch && !showForm && !isEditMode && (
              <div className="space-y-4">
                <div className="relative">
                  <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                  <Input
                    placeholder="Search existing contacts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    autoFocus
                  />
                </div>

                {/* Search results */}
                {debouncedSearch && (
                  <div className="space-y-2">
                    {isSearching ? (
                      <div className="flex justify-center py-4">
                        <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
                      </div>
                    ) : searchResults.length > 0 ? (
                      <div className="space-y-1">
                        {searchResults.map((contact) => (
                          <button
                            key={contact.id}
                            type="button"
                            className="hover:bg-accent w-full rounded-md border p-3 text-left transition-colors"
                            onClick={() => handleSelectExisting(contact)}
                          >
                            <div className="text-sm font-medium">
                              {getContactDisplayName(contact)}
                            </div>
                            {(contact.email || contact.phoneNumber) && (
                              <div className="text-muted-foreground mt-0.5 text-xs">
                                {[contact.email, contact.phoneNumber]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground py-2 text-center text-sm">
                        No contacts found
                      </p>
                    )}
                  </div>
                )}

                {/* Create New button */}
                <div className="border-t pt-4">
                  <Button
                    size="xs"
                    density="compact"
                    gap="standard"
                    width="full"
                    variant="outline"
                    onClick={() => setShowForm(true)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Create New Contact
                  </Button>
                </div>
              </div>
            )}

            {/*
            **Creating is the attribute treatment** (VALE-632) — the same
            buffered create form the Contacts drawer's add mode renders, so the
            two entry points into "add a contact" are one component and one
            field list.

            `isEditMode` still renders `ContactFormCard`: this dialog can in
            principle be handed an `existingContact` to update, which is an
            *edit* surface and a different conversion. No caller passes one
            today, so that arm is vestigial rather than load-bearing — but
            converting a path nothing exercises is not this ticket's job.
          */}
            {isEditMode ? (
              <ContactFormCard
                contact={contactData}
                mode="edit"
                onSave={handleSave}
                onCancel={handleCancel}
                isSubmitting={isSubmitting}
                showRoles={hasMatterId}
                showMatterNotes={hasMatterId}
              />
            ) : (
              showForm && (
                <ContactAttributeCreateForm
                  contact={contactData}
                  /*
                  `ContactFormCard`'s `isEstate` defaults to `true` and this
                  dialog never passed it, so the estate role branch is what it
                  has always rendered. Stated explicitly here rather than
                  inherited from a default, because the create form has no
                  default to inherit — resolving the matter's real type is a
                  behaviour change and belongs to its own ticket.
                */
                  isEstate
                  hasMatterContext={hasMatterId}
                  matterId={hasMatterId ? matterIdNumber : undefined}
                  isSubmitting={isSubmitting}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  onEditingChange={(editing) => {
                    editingFieldRef.current = editing;
                  }}
                  onDirtyChange={unsavedChanges.setIsDirty}
                />
              )
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/*
        A sibling of the sheet, not a child — the same shape
        `ContactDetailsDrawer` uses, so the discard prompt is never inside the
        layer it is asking about dismissing.
      */}
      <UnsavedChangesDialog
        open={unsavedChanges.showUnsavedDialog}
        onOpenChange={(next) => {
          unsavedChanges.setShowUnsavedDialog(next);
          // *Keep editing* returns the reader to the field they were stopped
          // leaving — Radix restores focus to the sheet body otherwise.
          if (!next) focusOpenAttributeEditor();
        }}
        onDiscard={unsavedChanges.handleDiscardChanges}
        itemLabel="contact"
      />
    </>
  );
}
